/*
 * BONQUERÉ — Client Care FAQ enhancements  (BNQ-WEB-0322 · Sprint 3.4.11)
 *
 * PURE PROGRESSIVE ENHANCEMENT. Everything on the FAQ page already works without this file: the
 * contents rail is server-rendered from the section's category blocks as real `<a href="#id">` links,
 * and every question is a native `<details>`/`<summary>` the browser opens on its own. Blocking or
 * failing to load this script costs the page nothing but the three refinements below.
 *
 * What it does, and nothing more:
 *   1. Marks the rail link for the category currently in view (`.is-active` + `aria-current`), via one
 *      IntersectionObserver. No scroll handler, no polling, no measurement of layout.
 *   2. On a rail click, scrolls with `prefers-reduced-motion` honoured and then moves focus to the
 *      target heading, so a keyboard or screen-reader user carries on reading from the same place a
 *      sighted user lands at. The offset itself belongs to CSS (`scroll-margin-top` on the headings),
 *      not to this file.
 *   3. Opens a deep-linked question. A `#faq-q-…` fragment addressing a collapsed `<details>` would
 *      otherwise land on a closed row in browsers that do not auto-expand for fragment navigation.
 *
 * NOT here, deliberately: no accordion implementation (the `<details>` element is the accordion), no
 * hand-written `aria-expanded` (the browser owns disclosure state — writing it by hand is what made
 * the old PDP accordion an accessibility defect, DEC-0025), no scroll-spy library, no third party, and
 * nothing legal — this file has no print path and never touches `[data-legal-*]`.
 *
 * THEME EDITOR SAFE. `shopify:section:load` re-initialises the section that was re-rendered, and any
 * observer belonging to the previous render of that section is disconnected first, so repeated edits
 * cannot accumulate observers. (Neither bnq-legal-toc.js nor bnq-size-guide.js does this; it is a new
 * requirement for this sprint, not an inherited pattern.)
 */
(function () {
  'use strict';

  // Observers are keyed by the root element they belong to, so a re-render can retire exactly the one
  // it replaces. A WeakMap lets a removed root be garbage-collected with its observer.
  var observers = new WeakMap();
  // Each root's active-category resolver, so the single `scrollend` listener below can settle the rail
  // without every re-init adding another listener of its own.
  var resolvers = new WeakMap();

  function prefersReducedMotion() {
    // Read per interaction rather than cached at load: a visitor can change the OS setting mid-session.
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Scrolls a target into view and hands focus to it — or, where the two differ, to `focusTarget`.
     Separating them matters for a question: the element that must be SCROLLED is the `<details>`, since
     that is what carries the `id` and therefore the `scroll-margin-top` offset, while the element that
     must be FOCUSED is its `<summary>`, since that is the real disclosure control. Scrolling the
     summary instead left a deep-linked question under the sticky header.

     `tabindex="-1"` makes a non-interactive target (a category heading) programmatically focusable
     without adding it to the tab order; a `<summary>` is already focusable and is left alone.
     `preventScroll` stops focus performing a second, un-offset jump on top of the smooth scroll. */
  function goTo(target, pushHash, focusTarget) {
    if (!target) return;

    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start'
    });

    var toFocus = focusTarget || target;
    if (!toFocus.hasAttribute('tabindex') && !/^(A|BUTTON|SUMMARY|INPUT|SELECT|TEXTAREA)$/.test(toFocus.tagName)) {
      toFocus.setAttribute('tabindex', '-1');
    }
    toFocus.focus({ preventScroll: true });

    if (pushHash && target.id && window.history && window.history.pushState) {
      window.history.pushState(null, '', '#' + target.id);
    }
  }

  /* Opens the `<details>` a fragment addresses, whether the fragment names the `<details>` itself or
     something inside it, and returns the row so the caller can scroll to it. Modern Chromium expands a
     targeted `<details>` by itself; Safari and Firefox have not always, and a deep link that lands on a
     closed answer looks broken either way, so this makes the behaviour the same everywhere. */
  function openDetailsFor(id) {
    if (!id) return null;

    var el = document.getElementById(id);
    if (!el) return null;

    var row = el.tagName === 'DETAILS' ? el : el.closest('details');
    if (!row) return null;

    row.open = true;
    return row;
  }

  function init(root) {
    if (!root) return;

    // Retire the previous render's observer before wiring this one.
    var previous = observers.get(root);
    if (previous) {
      previous.disconnect();
      observers.delete(root);
    }

    var nav = root.querySelector('[data-faq-nav]');
    var links = nav ? Array.prototype.slice.call(nav.querySelectorAll('[data-faq-link]')) : [];

    /* ── 1 · Rail clicks ─────────────────────────────────────────────────────────────────────────
       The links are genuine anchors, so doing nothing here would still work — this only adds the
       motion preference and the focus move. If the target is missing for any reason the default
       navigation is left alone rather than swallowed. */
    links.forEach(function (link) {
      // Guarded so repeated Theme Editor re-renders cannot stack duplicate handlers on the same link.
      if (link.dataset.faqBound === 'true') return;
      link.dataset.faqBound = 'true';
      link.addEventListener('click', function (event) {
        var heading = document.getElementById(link.getAttribute('data-faq-link'));
        if (!heading) return;
        event.preventDefault();

        /* Mark the clicked category active IMMEDIATELY, rather than waiting for the scroll to produce
           an observer boundary event. The user has just said which category they want, so this is both
           the semantically right answer and the only one that is not a race: a smooth scroll's duration
           scales with distance, and a fixed timeout guessing at it measurably set the WRONG category —
           a click on Payments left the rail on Returns & refunds, because at the moment the timeout
           fired Payments had not yet crossed the line. The geometric resolver then takes over as soon
           as the reader scrolls, and `scrollend` settles the final position exactly. */
        setActive(link.getAttribute('data-faq-link'));
        goTo(heading, true);
      });
    });

    /* ── 2 · Active category ─────────────────────────────────────────────────────────────────────
       The active category is the LAST ONE WHOSE HEADING HAS PASSED THE READING LINE — the line being
       the same sticky-header offset the anchors use, so "active" means exactly "the category you are
       reading". Before the first heading reaches the line (i.e. while the intro is still on screen)
       nothing is active, which matches the approved policy rail.

       WHY NOT A THIN INTERSECTION BAND. The obvious approach — and the one the legal rail uses — marks
       a heading active only while it sits inside a narrow band across the viewport. Measured on this
       page, that leaves the rail with NOTHING highlighted for most of the scroll: the band is about a
       tenth of the viewport, a heading crosses it in a few hundred milliseconds, and the long stretch
       of answers between two headings has no heading in the band at all. Landing on a category by deep
       link put the heading above the band and highlighted nothing.

       This still uses ONE IntersectionObserver and no scroll listener, because the answer can only
       change when some heading crosses the line — and that crossing is precisely the boundary event the
       observer fires on. The root's top edge is inset by the offset so the line IS that boundary. Each
       callback then reads six element positions, which is cheap and reads no layout it did not just
       invalidate. Where IntersectionObserver is unavailable the rail simply does not self-highlight and
       every link still works. */
    if (!('IntersectionObserver' in window) || !links.length) return;

    var headings = links
      .map(function (link) {
        return document.getElementById(link.getAttribute('data-faq-link'));
      })
      .filter(Boolean);

    if (!headings.length) return;

    function setActive(id) {
      links.forEach(function (link) {
        var isActive = link.getAttribute('data-faq-link') === id;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    /* The reading line, in pixels from the top of the viewport. Taken from the heading's own resolved
       `scroll-margin-top` rather than re-deriving it, so the line and the anchor offset are guaranteed
       to be the same number and cannot drift when the header height changes — the class of drift that
       put Shipping's headings under its nav bar (BNQ-WEB-0216). */
    function readingLine() {
      var v = parseFloat(getComputedStyle(headings[0]).scrollMarginTop);
      return isFinite(v) ? v : 96;
    }

    function resolveActive() {
      // Both of these are hoisted and reachable from the click handler declared above, which can run
      // after an early return left `headings` unset (no IntersectionObserver support). Guarded so a
      // click on an unenhanced rail cannot throw.
      if (!headings || !headings.length) return;

      var line = readingLine() + 2; // 2px tolerance for sub-pixel layout
      var current = null;
      for (var i = 0; i < headings.length; i += 1) {
        if (headings[i].getBoundingClientRect().top <= line) {
          current = headings[i];
        } else {
          break; // headings are in document order, so the first one below the line ends the search
        }
      }
      setActive(current ? current.id : null);
    }

    var observer = new IntersectionObserver(resolveActive, {
      rootMargin: '-' + Math.round(readingLine()) + 'px 0px 0px 0px',
      threshold: 0
    });

    headings.forEach(function (heading) {
      observer.observe(heading);
    });

    observers.set(root, observer);
    resolvers.set(root, resolveActive);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-faq]');
    Array.prototype.forEach.call(roots, init);
  }

  initAll(document);

  /* ONE `scrollend` listener for the page, not one per re-init. It settles the rail at the exact moment
     a scroll finishes — including the smooth scroll a rail click starts — which is the honest version of
     the fixed timeout this replaced. Where `scrollend` is unsupported (older Safari) the click's own
     immediate `setActive` already left the rail correct and the observer handles everything after, so
     nothing here is load-bearing. */
  if ('onscrollend' in window) {
    window.addEventListener('scrollend', function () {
      var roots = document.querySelectorAll('[data-faq]');
      Array.prototype.forEach.call(roots, function (root) {
        var resolve = resolvers.get(root);
        if (resolve) resolve();
      });
    });
  }

  /* ── 3 · Deep links ──────────────────────────────────────────────────────────────────────────
     Runs once at load, and again on every subsequent hash change so an in-page link to a question
     behaves the same as arriving with the fragment already in the URL. A frame's delay lets layout
     settle after the row opens, so the `scroll-margin-top` offset lands the row correctly rather than
     against its pre-expansion position. */
  function honourHash() {
    if (window.location.hash.length < 2) return;

    var row = openDetailsFor(window.location.hash.slice(1));
    if (!row) return;

    window.requestAnimationFrame(function () {
      // Scroll the row (it owns the id and the offset), focus the summary (it is the real disclosure
      // control, so a keyboard user arriving by deep link can immediately collapse the answer again).
      goTo(row, false, row.querySelector('summary'));
    });
  }

  honourHash();
  window.addEventListener('hashchange', honourHash);

  // Theme Editor: re-wire only the section that was re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
